import { Component, HostBinding, OnDestroy, ViewEncapsulation } from '@angular/core';
import { ErrorStateMatcher, MatOptionModule } from '@angular/material/core';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { Subscription } from 'rxjs';
import { MatRadioChange, MatRadioModule } from '@angular/material/radio';
import {
	FormControl,
	FormGroup,
	FormGroupDirective,
	NgForm,
	Validators,
	FormsModule,
	ReactiveFormsModule
} from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';
import { NgIf, NgFor } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';

import { CoilCalculator, CoilCalculatorResult } from '../models/coil-calculator';
import { pageTransitions } from '../../shared/animations/page-transitions';
import { LocationService } from '../../shared/services/location.service';
import { CoilCalculatorService } from '../services/coil-calculator.service';
import {
	AdditionalDataChoices, HOT_OR_COLD_ROLLED, METALLIC_COATING_TYPE_OPTIONS,
	METALLIC_COATING_TYPE_OPTIONS_KEYS, ORGANIC_COATED_STEEL, innerDiameterOptions
} from '../models/coil-calculator-constants';
import { NumberPipe } from '../../shared/pipes/number.pipe';
import { LoadingSpinnerComponent } from '../../shared/components/utils/loading-spinner.component';
import { InputNumberDirective } from '../../shared/directives/input-number.directive';
import { PageHeaderComponent } from '../../shared/components/page-header/page-header.component';

/** Error when invalid control is dirty, touched, or submitted. */
class InnerOuterFormGroupErrorStateMatcher extends ErrorStateMatcher {
	override isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null) {
		return super.isErrorState(control, form)
			|| (form !== null ? form.hasError('inner-greater-than-outer') : false);
	}
}

const metallicCoatingTypeValues = METALLIC_COATING_TYPE_OPTIONS_KEYS.map(key => ({
	key, value: METALLIC_COATING_TYPE_OPTIONS[key]
}));

@Component({
	selector: 'copo-coil-calculator',
	templateUrl: './coil-calculator.component.html',
	styleUrl: './coil-calculator.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		PageHeaderComponent,
		TranslateModule,

		MatButtonModule,
		FormsModule,
		ReactiveFormsModule,

		MatFormFieldModule,

		MatInputModule,
		InputNumberDirective,
		MatIconModule,
		NgIf,

		MatSelectModule,

		MatOptionModule,
		NgFor,

		MatRadioModule,
		LoadingSpinnerComponent,
		NumberPipe
	]
})
export class CoilCalculatorComponent implements OnDestroy {
	@HostBinding('class.sidenav-content') sidenavContent = true;

	isLoading = false;
	coilCalculator: CoilCalculator = new CoilCalculator();
	coilCalculatorResult: CoilCalculatorResult = new CoilCalculatorResult();

	readonly metallicCoatingTypeValues = metallicCoatingTypeValues;
	readonly innerDiameterOptionsValues = innerDiameterOptions;
	instantMatcher = new InnerOuterFormGroupErrorStateMatcher();
	coilCalculatorResultSubscription: Subscription | undefined;

	nominalThicknessFormControl = new FormControl<number | null>(null, [
		Validators.required,
		formControl => formControl.value > 0 ? null : { required: true }
	]);

	metallicCoatingTypeFormControl = new FormControl<number | null>(null, [
		Validators.required,
		formControl => formControl.value > 0 ? null : { required: true }
	]);

	metallicCoatingThicknessFormControl = new FormControl<number | null>(null);
	totalPaintThicknessFormControl = new FormControl<number | null>(null);
	organicCoatedFilmThicknessFormControl = new FormControl<number | null>(null);
	innerDiameterFormControl = new FormControl<number | null>(null, [
		Validators.required,
		formControl => formControl.value > 0 ? null : { required: true }
	]);

	widthFormControl = new FormControl<number | null>(null, [
		Validators.required,
		formControl => formControl.value > 0 ? null : { required: true }
	]);

	additionalDataChoiceFormControl = new FormControl<string | null>(null, [Validators.required]);
	additionalDataOuterDiameterFormControl = new FormControl<number | null>(null);
	additionalDataWeightFormControl = new FormControl<number | null>(null);
	additionalDatLengthFormControl = new FormControl<number | null>(null);
	additionalDataSpecificWeightFormControl = new FormControl<number | null>(null);

	coilCalculatorFormGroup = new FormGroup({
		nominalThickness: this.nominalThicknessFormControl,
		metallicCoatingTypeDensity: this.metallicCoatingTypeFormControl,
		metallicCoatingThickness: this.metallicCoatingThicknessFormControl,
		totalPaintThickness: this.totalPaintThicknessFormControl,
		organicCoatedFilmThickness: this.organicCoatedFilmThicknessFormControl,
		innerDiameter: this.innerDiameterFormControl,
		width: this.widthFormControl,
		additionalDataChoice: this.additionalDataChoiceFormControl,
		additionalDataOuter: this.additionalDataOuterDiameterFormControl,
		additionalDataWeight: this.additionalDataWeightFormControl,
		additionalDataLength: this.additionalDatLengthFormControl,
		additionalDataSpecificWeight: this.additionalDataSpecificWeightFormControl,
	}, {
		validators: () => {
			if (this.additionalDataChoiceFormControl.value === 'coilOuterDiameter'
				&& this.additionalDataOuterDiameterFormControl.value && this.innerDiameterFormControl.value
				&& this.innerDiameterFormControl.value >= this.additionalDataOuterDiameterFormControl.value) {
				return {
					// eslint-disable-next-line @typescript-eslint/naming-convention
					'inner-greater-than-outer': true
				};
			}
			return null;
		}
	});

	constructor(
		private readonly locationService: LocationService,
		private readonly coilCalculatorService: CoilCalculatorService
	) {}

	ngOnDestroy(): void {
		this.coilCalculatorResultSubscription?.unsubscribe();
	}

	goBack() {
		this.locationService.go(-1);
	}

	organicCoatedSteelSelected() {
		this.coilCalculator.calculationType = ORGANIC_COATED_STEEL;

		this.metallicCoatingTypeFormControl.setValidators([
			Validators.required,
			formControl => formControl.value > 0 ? null : { required: true }
		]);
		this.coilCalculator.metallicCoatingTypeDensity = this.metallicCoatingTypeFormControl.value ?? 0;
		this.metallicCoatingTypeFormControl.updateValueAndValidity();
		this.fetchResult();
	}

	hotOrColdRolledSelected() {
		this.coilCalculator.calculationType = HOT_OR_COLD_ROLLED;

		this.metallicCoatingTypeFormControl.setValidators([]);
		this.metallicCoatingTypeFormControl.updateValueAndValidity();
		this.fetchResult();
	}

	nominalThicknessChanged(event: Event) {
		const target = <HTMLInputElement>event.target;
		this.coilCalculator.nominalThickness = target.value ? Number(Number.parseFloat(target.value).toFixed(2)) : 0;
		this.fetchResult();
	}

	metallicCoatingTypeChanged(event: MatSelectChange) {
		this.coilCalculator.metallicCoatingTypeDensity = <number>event.value;
		this.fetchResult();
	}

	metallicCoatingThicknessChanged(event: Event) {
		const target = <HTMLInputElement>event.target;
		this.coilCalculator.metallicCoatingThickness = target.value
			? Number(Number.parseFloat(target.value).toFixed(2))
			: 0;
		this.fetchResult();
	}

	totalPaintThicknessChanged(event: Event) {
		const target = <HTMLInputElement>event.target;
		this.coilCalculator.totalPaintThickness = target.value ? Number(Number.parseFloat(target.value).toFixed(2)) : 0;
		this.fetchResult();
	}

	organicCoatedFilmThicknessChanged(event: Event) {
		const target = <HTMLInputElement>event.target;
		this.coilCalculator.organicCoatedFilmThickness = target.value
			? Number(Number.parseFloat(target.value).toFixed(2))
			: 0;
		this.fetchResult();
	}

	innerDiameterChanged(event: MatSelectChange) {
		this.coilCalculator.innerDiameter = <number>event.value;
		this.fetchResult();
	}

	widthChanged(event: Event) {
		const target = <HTMLInputElement>event.target;
		this.coilCalculator.width = target.value ? Number(Number.parseFloat(target.value)) : 0;
		this.fetchResult();
	}

	additionalDataChoiceChanged(event: MatRadioChange) {
		if (!(event instanceof MatRadioChange) || event.source.name !== 'additional-coil-data') {
			return;
		}
		this.coilCalculator.additionalDataChoice = <AdditionalDataChoices>event.value;

		// update additional data value if already exists or set validators for additional data form control
		// coilOuterDiameter
		if (this.coilCalculator.additionalDataChoice === 'coilOuterDiameter') {
			this.additionalDataOuterDiameterFormControl.setValidators([
				Validators.required,
				formControl => formControl.value > 0 ? null : { required: true }
			]);
			this.coilCalculator.additionalData = this.additionalDataOuterDiameterFormControl.value ?? 0;
		} else {
			this.additionalDataOuterDiameterFormControl.setValidators([]);
		}
		this.additionalDataOuterDiameterFormControl.updateValueAndValidity();

		// coilWeight
		if (this.coilCalculator.additionalDataChoice === 'coilWeight') {
			this.additionalDataWeightFormControl.setValidators([
				Validators.required,
				formControl => formControl.value > 0 ? null : { required: true }
			]);
			this.coilCalculator.additionalData = this.additionalDataWeightFormControl.value ?? 0;
		} else {
			this.additionalDataWeightFormControl.setValidators([]);
		}
		this.additionalDataWeightFormControl.updateValueAndValidity();

		// coilLength
		if (this.coilCalculator.additionalDataChoice === 'coilLength') {
			this.additionalDatLengthFormControl.setValidators([
				Validators.required,
				formControl => formControl.value > 0 ? null : { required: true }
			]);
			this.coilCalculator.additionalData = this.additionalDatLengthFormControl.value ?? 0;
		} else {
			this.additionalDatLengthFormControl.setValidators([]);
		}
		this.additionalDatLengthFormControl.updateValueAndValidity();

		// specificWeight
		if (this.coilCalculator.additionalDataChoice === 'specificWeight') {
			this.additionalDataSpecificWeightFormControl.setValidators([
				Validators.required,
				formControl => formControl.value > 0 ? null : { required: true }
			]);
			this.coilCalculator.additionalData = this.additionalDataSpecificWeightFormControl.value ?? 0;
		} else {
			this.additionalDataSpecificWeightFormControl.setValidators([]);
		}
		this.additionalDataSpecificWeightFormControl.updateValueAndValidity();

		this.fetchResult();
	}

	additionalDataChanged(event: Event) {
		const target = <HTMLInputElement>event.target;
		this.coilCalculator.additionalData = target.value ? Number(Number.parseFloat(target.value).toFixed(3)) : 0;
		this.fetchResult();
	}

	fetchResult() {
		if (!this.coilCalculatorFormGroup.valid) {
			this.coilCalculatorResult.resultCalculated = false;
			return;
		}
		this.coilCalculatorResultSubscription?.unsubscribe();
		this.isLoading = true;
		this.coilCalculatorResultSubscription = this.coilCalculatorService.coilCalculatorResult(this.coilCalculator)
			.subscribe({
				next: data => {
					this.coilCalculatorResult = data;
					this.isLoading = false;
				},
				error: (err: unknown) => {
					console.error(err);
				},
			});
	}
}
